import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  DOC_TYPE_LABEL,
  DOC_TYPES,
  DocType,
  documentsApi,
  isExemptionDocType,
} from '../lib/documents-api';

/**
 * Extensions the server's magic-byte allowlist actually accepts. Rejecting here is a
 * courtesy that saves a 25 MB round-trip; the SERVER's sniff is the real gate, and it
 * reads the bytes rather than trusting this list or the browser's mimetype.
 */
const ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.tif,.tiff,.doc,.docx,.xls,.xlsx,.pptx';
const MAX_BYTES = 25 * 1024 * 1024;

/**
 * Starter tag per type — a SEED you extend, not the final value.
 *
 * On its own a type tag adds no filtering power (the type chips and doc_type already do
 * that), so its whole job is to leave you with `contract, ` to build on instead of a blank
 * box, and to give every document one predictable search term. 'other' seeds nothing —
 * there is no honest word for it, and inventing one would be noise.
 *
 * This seed is the ONLY tag writing this dialog does. There is deliberately no curated-tag
 * chip row here: picking the Non-Profit TYPE already seeds `non-profit`, and the one tag
 * nobody types by hand is written server-side by Apply Coupon. Filtering by it lives on the
 * Documents tab, which is where you go looking for it.
 */
const TYPE_TAG_SEED: Record<DocType, string> = {
  contract: 'contract',
  nda: 'nda',
  tax_exempt: 'tax-exempt',
  // The type already says it; the seed makes the same word searchable and gives the curated
  // chip below something to agree with, so a document filed under the type and one TAGGED by
  // Apply Coupon end up findable by the same term.
  non_profit: 'non-profit',
  tax_withholding: 'tax-withholding',
  ownership_transfer: 'ownership-transfer',
  po: 'po',
  other: '',
};

interface Props {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded: () => void;
}

export function DocumentUploadDialog({ accountId, open, onOpenChange, onUploaded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<DocType>('contract');
  const [title, setTitle] = useState('');
  const [externalNumber, setExternalNumber] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [tags, setTags] = useState(TYPE_TAG_SEED.contract);
  // Once you edit tags yourself, changing type must not overwrite your words.
  const [tagsTouched, setTagsTouched] = useState(false);
  const [notes, setNotes] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setTitle('');
    setExternalNumber('');
    setJurisdiction('');
    setTags(TYPE_TAG_SEED.contract);
    setTagsTouched(false);
    setNotes('');
    setDragOver(false);
  };

  const acceptFile = (f: File | undefined) => {
    if (!f) return;
    const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPT.split(',').includes(ext)) {
      toast.error('Unsupported file type', { description: `Allowed: ${ACCEPT}` });
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error('File too large', { description: 'The limit is 25 MB.' });
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    if (!file) {
      toast.error('Choose a file first');
      return;
    }
    setBusy(true);
    try {
      const { stripe } = await documentsApi.upload(
        {
          accountId,
          docType,
          title: title.trim() || undefined,
          externalNumber: externalNumber.trim() || undefined,
          jurisdiction: jurisdiction.trim() || undefined,
          notes: notes.trim() || undefined,
          tags: tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
        },
        file,
      );
      // Filing an exemption certificate can stop the customer being taxed. That is a billing
      // change the operator did not explicitly ask for, so it is SAID — a silent tax change
      // discovered later on an invoice is the failure mode worth spending a toast line on.
      // Only when it actually moved: a customer already Exempt has nothing to report.
      if (stripe?.taxStatusChanged) {
        toast.success('Document uploaded — Stripe Tax status set to Exempt', {
          description:
            'Future invoices for this customer will not be taxed, in every jurisdiction. ' +
            'Remove the exemption in Stripe if that is not intended.',
        });
      } else {
        toast.success('Document uploaded');
      }
      reset();
      onOpenChange(false);
      onUploaded();
    } catch (e: unknown) {
      toast.error('Upload failed', { description: (e as Error)?.message ?? 'Unknown error' });
    } finally {
      setBusy(false);
    }
  };

  // A certificate's validity is state-specific, so the two exemption types are the ones that
  // ask for jurisdiction — a state non-profit exemption is as state-bound as a resale
  // certificate. The other three do not.
  const isTaxExempt = isExemptionDocType(docType);

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : (reset(), onOpenChange(o)))}>
      <DialogContent className="max-w-2xl" data-testid="document-upload-dialog">
        <DialogHeader>
          <DialogTitle>{'Upload customer document'}</DialogTitle>
          <DialogDescription>
            {'Stored privately. Every download is recorded against your account.'}
          </DialogDescription>
        </DialogHeader>

        <div
          role="button"
          tabIndex={0}
          data-testid="document-dropzone"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            acceptFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-6 text-sm transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
          }`}
        >
          {file ? (
            <span data-testid="document-selected-file">
              {file.name}
              {' · '}
              {(file.size / 1024 / 1024).toFixed(2)}
              {' MB'}
            </span>
          ) : (
            <span className="text-muted-foreground">
              {'Drag a file here, or click to choose · PDF, images, Word, Excel · max 25 MB'}
            </span>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            data-testid="document-file-input"
            onChange={(e) => acceptFile(e.target.files?.[0] ?? undefined)}
          />
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-4">
          <div className="space-y-1">
            <Label>{'Type'}</Label>
            <Select
              value={docType}
              onValueChange={(v) => {
                const next = v as DocType;
                setDocType(next);
                if (!tagsTouched) setTags(TYPE_TAG_SEED[next]);
              }}
            >
              <SelectTrigger data-testid="document-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {DOC_TYPE_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>{'Title'}</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Defaults to the filename"
              data-testid="document-title-input"
            />
          </div>

          {/*
            Only fields that earn their place, per type.

            NOTE effective/expiration date were removed from upload on request. The columns
            and the whole expiry apparatus remain — the custops_document_status view, the
            expiring index and GET /documents/expiring all still work — but nothing captures
            a date at upload, so hand-uploaded documents read "Valid" forever and the expiry
            queue stays empty until a date arrives via PATCH or the bulk import. Restoring
            the field is the only thing needed to switch it back on.

            Counterparty was dropped: the document already hangs off a customer, so for our
            contracts the counterparty IS that customer and the field just asks you to retype
            the company you navigated through. Governing law was dropped for the same reason
            in reverse — real but lawyer-grade, and a field nobody fills is worse than no
            field, because the record then looks incomplete rather than deliberately blank.
            Both columns remain in the schema for the future bulk import to populate.
          */}
          {docType !== 'other' ? (
            <div className="space-y-1">
              <Label>
                {docType === 'po'
                  ? 'PO number'
                  : isTaxExempt
                    ? 'Certificate number'
                    : 'Reference number'}
              </Label>
              <Input
                value={externalNumber}
                onChange={(e) => setExternalNumber(e.target.value)}
                placeholder="The PO number or certificate number"
                data-testid="document-number-input"
              />
            </div>
          ) : null}

          {/* A certificate is valid PER STATE, so this decides whether it is any use at
              all. Meaningless on the other three types, so it only appears here. */}
          {isTaxExempt ? (
            <div className="space-y-1">
              <Label>{'State'}</Label>
              <Input
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                placeholder="TX"
                data-testid="document-jurisdiction-input"
              />
            </div>
          ) : null}



          <div className="space-y-1">
            <Label>{'Tags'}</Label>
            <Input
              value={tags}
              onChange={(e) => {
                setTags(e.target.value);
                setTagsTouched(true);
              }}
              placeholder="comma,separated"
              data-testid="document-tags-input"
            />
          </div>
        </div>

        <div className="mt-3 space-y-1">
          <Label>{'Notes'}</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="document-notes-input"
          />
        </div>

        {/*
          Standing warning, not a conditional one. These documents legitimately contain
          tax IDs and signatures; card numbers are the one class that must never land here,
          because this store is out of PCI scope.
        */}
        <p
          className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400"
          data-testid="document-pan-notice"
        >
          {'Do not upload payment card numbers. Redact them before uploading.'}
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {'Cancel'}
          </Button>
          <Button onClick={submit} disabled={busy || !file} data-testid="document-upload-submit">
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
